// TODO: Decommission this.
//       Except for cleanFilterParams function and FilterParamsPartial and FilterParamsKey types.
// Helpers to convert searchParams (from useSearchParams hook) to GetShipmentsParams and back.
import {
	type GetShipmentsParams as FilterParams,
	GetShipmentsFilterCarrierAssignedSubStatusItem,
	GetShipmentsFilterCocStatusItem,
	GetShipmentsFilterContainerSizesItem,
	GetShipmentsFilterStatusesItem,
	GetShipmentsFilterTransportTypesItem,
	GetShipmentsSortBy,
	GetShipmentsSortOrder,
} from '@uturn/api/v3';

type ConstEnum = Record<string, string>;
type FilterParamsPartial = Partial<FilterParams>;
type FilterParamsKey = keyof FilterParams;
type ParsePrimitives = {
	type: 'string' | 'number' | 'boolean';
	array: boolean;
};
type ParseConstEnums<T extends ConstEnum> = {
	type: 'enum';
	array: boolean;
	constEnum: T;
};
type ParseOptions<T extends ConstEnum> = ParsePrimitives | ParseConstEnums<T>;

export const SHIPMENTS_SEARCH_PARAMS_EMPTY: FilterParamsPartial = {} as const;

export function isConstEnumType<T extends ConstEnum>(
	value: unknown,
	constEnum: T,
): value is T[keyof T] {
	return Object.values(constEnum).includes(value as T[keyof T]);
}

export function parseString(value: string | null) {
	return value ?? undefined;
}

export function parseStringArray(values: string[] | null) {
	if (!values?.length) {
		return undefined;
	}

	const result = values.map(parseString).filter((value) => value !== undefined);

	if (!result.length) {
		return undefined;
	}

	return result;
}

export function parseNumber(value: string | null) {
	if (value == null) {
		return undefined;
	}

	const result = Number(value);

	if (Number.isNaN(result)) {
		return undefined;
	}

	return result;
}

export function parseNumberArray(values: string[] | null) {
	if (!values?.length) {
		return undefined;
	}

	const result = values.map(parseNumber).filter((value) => value !== undefined);

	if (!result.length) {
		return undefined;
	}

	return result;
}

export function parseBoolean(value: string | null) {
	if (value) {
		return value.trim().toLowerCase() === 'true' || value.trim() === '1';
	}

	return undefined;
}

export function parseConstEnum<T extends ConstEnum>(
	value: string | null,
	constEnum: T,
) {
	if (value && isConstEnumType(value, constEnum)) {
		return value;
	}

	return undefined;
}

export function parseConstEnumArray<T extends ConstEnum>(
	values: string[] | null,
	constEnum: T,
) {
	if (!values?.length) {
		return undefined;
	}

	const result = values
		.map((value) => parseConstEnum(value, constEnum))
		.filter((value) => value !== undefined);

	if (!result.length) {
		return undefined;
	}

	return result;
}

export function parseAnySearchParamFn<T extends ConstEnum>(
	val: string | string[],
	options: ParseOptions<T>,
) {
	if (typeof val === 'string') {
		switch (options.type) {
			case 'string':
				return parseString(val);
			case 'number':
				return parseNumber(val);
			case 'boolean':
				return parseBoolean(val);
			case 'enum':
				return parseConstEnum(val, options.constEnum);
			default:
				return undefined;
		}
	}

	switch (options.type) {
		case 'string':
			return parseStringArray(val);
		case 'number':
			return parseNumberArray(val);
		case 'boolean':
			throw new Error(`Unsupported array of ${options.type}!`);
		case 'enum':
			return parseConstEnumArray(val, options.constEnum);
		default:
			return undefined;
	}
}

export function parseAnyFilterParamFromSearchParams<T extends ConstEnum>(
	searchParams: URLSearchParams,
	key: FilterParamsKey,
	options: ParseOptions<T>,
) {
	if (options.array) {
		const values = searchParams.getAll(key);
		return !values.length
			? SHIPMENTS_SEARCH_PARAMS_EMPTY
			: { [key]: parseAnySearchParamFn(values, options) };
	}
	const value = searchParams.get(key);
	return value === null
		? SHIPMENTS_SEARCH_PARAMS_EMPTY
		: { [key]: parseAnySearchParamFn(value, options) };
}

/** Partial version of parseFilterParamsFromSearchParams. */
export function parsePartialFilterParamsFromSearchParams(
	searchParams: URLSearchParams,
) {
	const filters: FilterParamsPartial = {
		...parseAnyFilterParamFromSearchParams(searchParams, 'page', {
			type: 'string',
			array: false,
		}),
		...parseAnyFilterParamFromSearchParams(searchParams, 'size', {
			type: 'string',
			array: false,
		}),
		...parseAnyFilterParamFromSearchParams(searchParams, 'sortBy', {
			type: 'enum',
			array: false,
			constEnum: GetShipmentsSortBy,
		}),
		...parseAnyFilterParamFromSearchParams(searchParams, 'sortOrder', {
			type: 'enum',
			array: false,
			constEnum: GetShipmentsSortOrder,
		}),
		...parseAnyFilterParamFromSearchParams(
			searchParams,
			'filter.transportTypes',
			{
				type: 'enum',
				array: true,
				constEnum: GetShipmentsFilterTransportTypesItem,
			},
		),
		...parseAnyFilterParamFromSearchParams(
			searchParams,
			'filter.containerSizes',
			{
				type: 'enum',
				array: true,
				constEnum: GetShipmentsFilterContainerSizesItem,
			},
		),
		...parseAnyFilterParamFromSearchParams(
			searchParams,
			'filter.fromDateTime',
			{
				type: 'string',
				array: false,
			},
		),
		...parseAnyFilterParamFromSearchParams(
			searchParams,
			'filter.untilDateTime',
			{
				type: 'string',
				array: false,
			},
		),
		...parseAnyFilterParamFromSearchParams(searchParams, 'filter.statuses', {
			type: 'enum',
			array: true,
			constEnum: GetShipmentsFilterStatusesItem,
		}),
		...parseAnyFilterParamFromSearchParams(searchParams, 'filter.countries', {
			type: 'string',
			array: true,
		}),
		...parseAnyFilterParamFromSearchParams(searchParams, 'filter.userIds', {
			type: 'number',
			array: true,
		}),
		...parseAnyFilterParamFromSearchParams(
			searchParams,
			'filter.requirements',
			{
				type: 'string',
				array: true,
			},
		),
		...parseAnyFilterParamFromSearchParams(
			searchParams,
			'filter.shippingLines',
			{
				type: 'string',
				array: true,
			},
		),
		...parseAnyFilterParamFromSearchParams(searchParams, 'filter.cocStatus', {
			type: 'enum',
			array: true,
			constEnum: GetShipmentsFilterCocStatusItem,
		}),
		...parseAnyFilterParamFromSearchParams(searchParams, 'filter.groups', {
			type: 'number',
			array: true,
		}),
		...parseAnyFilterParamFromSearchParams(
			searchParams,
			'filter.onlyContractShipments',
			{
				type: 'boolean',
				array: false,
			},
		),
		...parseAnyFilterParamFromSearchParams(
			searchParams,
			'filter.carrierAssignedSubStatus',
			{
				type: 'enum',
				array: true,
				constEnum: GetShipmentsFilterCarrierAssignedSubStatusItem,
			},
		),
	};

	return filters;
}

export function parseSearchParamsFromFilterParams(
	filters: FilterParamsPartial,
) {
	const searchParams = new URLSearchParams();

	Object.entries(filters).forEach(([k, v]) => {
		const key = k as FilterParamsKey;
		const val = v as FilterParams[FilterParamsKey];

		if (val === undefined) {
			return;
		}

		if (typeof val === 'object' && Array.isArray(val)) {
			if (!val.length) {
				return;
			}

			val.forEach((value) => searchParams.append(key, String(value)));
			return;
		}

		searchParams.set(key, String(val));
	});

	return searchParams;
}

/** Returns `Shipments Search Params` without keys that are undefined or empty array. */
export function cleanFilterParams(filters: FilterParamsPartial) {
	let newFilters: FilterParamsPartial = {};

	Object.entries(filters).forEach(([k, v]) => {
		const key = k as FilterParamsKey;
		const val = v as FilterParams[FilterParamsKey];

		// Preserve an explicitly-emptied status selection. On tabs with a status
		// filter (e.g. Published, Assigned) an empty `filter.statuses` means
		// "nothing selected" and must survive instead of being dropped —
		// otherwise the tab defaults get re-applied downstream and bulk cancel
		// would target every shipment in the tab. Every other empty array is
		// still stripped.
		if (
			val === undefined ||
			(key !== 'filter.statuses' &&
				typeof val === 'object' &&
				Array.isArray(val) &&
				!val.length)
		) {
			return;
		}

		newFilters = {
			...newFilters,
			[key]: val,
		};
	});

	return newFilters;
}

export const toNumberArray = (val: unknown): number[] => {
	if (Array.isArray(val)) {
		return val.map(Number).filter((n) => !Number.isNaN(n));
	}
	if (val != null) {
		const n = Number(val);
		return Number.isNaN(n) ? [] : [n];
	}
	return [];
};
